import { z } from 'zod';

export const FoodTruckStopStatusSchema = z.enum([
  'scheduled',
  'en_route',
  'on_site',
  'completed',
  'canceled',
]);

export const FoodTruckStopSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string(),
  truckId: z.string(),
  label: z.string(),
  status: FoodTruckStopStatusSchema,
  startAt: z.string().datetime(),
  endAt: z.string().datetime(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  addressLine1: z.string().nullable().optional(),
  addressLine2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  countryCode: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  windowNotes: z.string().nullable().optional(),
  serviceRadiusMeters: z.number().nullable().optional(),
  curbsideEnabled: z.boolean(),
  preArrivalChecklist: z.array(z.unknown()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const FoodTruckStopListResponseSchema = z.object({
  ok: z.literal(true),
  stops: z.array(FoodTruckStopSchema),
});

export const FoodTruckStopTransitionResponseSchema = z.object({
  ok: z.literal(true),
  stop: FoodTruckStopSchema,
});

export const CurbsideStatusSchema = z.enum([
  'pending',
  'arrived',
  'parked',
  'complete',
  'canceled',
  'no_show',
]);

export const CurbsideCheckinSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string(),
  locationId: z.string(),
  orderId: z.string().nullable(),
  status: CurbsideStatusSchema,
  vehicleDescription: z.string().nullable(),
  vehicleColor: z.string().nullable(),
  licensePlate: z.string().nullable(),
  checkinMethod: z.string(),
  parkedSpot: z.string().nullable(),
  notes: z.string().nullable(),
  etaMinutes: z.number().nullable(),
  arrivalEta: z.string().datetime().nullable(),
  arrivedAt: z.string().datetime().nullable(),
  parkedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  canceledAt: z.string().datetime().nullable(),
  clientRequestId: z.string().nullable(),
  acknowledgedBy: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const OfflineDrillStatusSchema = z.enum(['scheduled', 'running', 'completed', 'failed']);

export const OfflineDrillSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string(),
  scenario: z.string(),
  status: OfflineDrillStatusSchema,
  initiatedBy: z.string().nullable(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  notes: z.string().nullable(),
  metrics: z.record(z.string(), z.unknown()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const FeatureFlagSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string(),
  locationId: z.string().nullable(),
  key: z.string(),
  value: z.record(z.string(), z.unknown()),
  isActive: z.boolean(),
  rolloutStrategy: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const RemoteConfigSchema = z.object({
  features: z.record(z.unknown()),
});

const RushTrendSchema = z.enum(['up', 'down', 'flat']);
const RushLevelSchema = z.enum(['nominal', 'watch', 'critical']);
const RushGuardrailSchema = z.object({
  id: z.string(),
  label: z.string(),
  severity: z.enum(['warning', 'critical']),
  description: z.string(),
});
const RushAfterActionSchema = z.object({
  id: z.string(),
  type: z.enum(['alert', 'note']),
  message: z.string(),
  occurredAt: z.number(),
});
const RushChannelMixSchema = z.object({
  channel: z.string(),
  orders: z.number(),
  percentage: z.number(),
});
const RushStationLoadSchema = z.object({
  stationId: z.string().nullable(),
  name: z.string(),
  load: z.number(),
  throttled: z.boolean(),
});
const RushInfrastructureSchema = z.object({
  routersOffline: z.number(),
  offlineQueueSize: z.number(),
  offlineRisk: RushLevelSchema,
  lteUsageMb: z.number(),
});
const RushStaffingSchema = z.object({
  active: z.number(),
  recommended: z.number(),
  coverage: RushLevelSchema,
  gaps: z.array(z.string()),
});
const RushForecastSchema = z.object({
  next15MinutesOrders: z.number(),
  confidence: z.number(),
  recommendation: z.string(),
});
const RushThroughputSchema = z.object({
  ordersPerMinute: z.number(),
  targetPerMinute: z.number(),
  trend: RushTrendSchema,
  waitTimeDeltaSeconds: z.number(),
  backlogCount: z.number(),
});
const RushStatusSchema = z.object({
  level: RushLevelSchema,
  reasons: z.array(z.string()),
});
const RushOverrideStateSchema = z.object({
  source: z.literal('manual'),
  level: RushLevelSchema,
  expiresAt: z.number(),
  reason: z.string().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
});
export const RushInsightsSchema = z.object({
  generatedAt: z.number(),
  mock: z.boolean(),
  status: RushStatusSchema,
  throughput: RushThroughputSchema,
  channelMix: z.array(RushChannelMixSchema),
  stationLoad: z.array(RushStationLoadSchema),
  infrastructure: RushInfrastructureSchema,
  staffing: RushStaffingSchema,
  parBreaches: z.array(RushGuardrailSchema),
  guardrails: z.array(RushGuardrailSchema),
  afterAction: z.array(RushAfterActionSchema),
  forecast: RushForecastSchema,
  override: RushOverrideStateSchema.nullable().optional(),
});
export type RushInsights = z.infer<typeof RushInsightsSchema>;

export const OfflineQueuedResultSchema = z.object({
  queued: z.literal(true),
  queueId: z.string().min(1),
  meta: z.record(z.unknown()).optional(),
});

export type FoodTruckStop = z.infer<typeof FoodTruckStopSchema>;
export type CurbsideCheckin = z.infer<typeof CurbsideCheckinSchema>;
export type OfflineDrill = z.infer<typeof OfflineDrillSchema>;
export type FeatureFlag = z.infer<typeof FeatureFlagSchema>;
export type RemoteConfig = z.infer<typeof RemoteConfigSchema>;
export type RushInsightsResponse = z.infer<typeof RushInsightsSchema>;
export type FoodTruckStopListResponse = z.infer<typeof FoodTruckStopListResponseSchema>;
export type FoodTruckStopTransitionResponse = z.infer<typeof FoodTruckStopTransitionResponseSchema>;
export type OfflineQueuedResponse = z.infer<typeof OfflineQueuedResultSchema>;
